import { useState } from "react";
import { Paper, Box, Typography, Divider, Stack, Chip, Button, Radio, RadioGroup, FormControlLabel, CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import RichEditor from "../../../components/rich-editor";
import ConfirmationModal from "../../../components/confirmation-modal";
import { generateButtonSx } from "../../../utils/elementor-style-override";
import useAgentPreferenceForAgent from "../../Settings/components/AgentPreferences/hooks/useAgentPreferenceForAgent";

const EMPTY_CONTENT = "<p></p>";

const STATUS_CONFIG = {
	Pending: { label: "Pending Approval", bg: "#FCEFD6", color: "#9C6B0A" },
	Approved: { label: "Approved", bg: "#E5F5EC", color: "#1E7E4D" },
	Rejected: { label: "Rejected", bg: "#FBEAE7", color: "#A12A2A" },
};

const approveButtonSx = {
	backgroundColor: "#1E7E4D !important",
	color: "#FFFFFF !important",
	fontWeight: 700,
	...generateButtonSx({ bg: "#16663D !important", text: "#FFFFFF" }, { bg: "#16663D !important", text: "#FFFFFF" }),
};

const rejectButtonSx = {
	color: "#B93544 !important",
	backgroundColor: "#FFF0F2 !important",
	fontWeight: 700,
	...generateButtonSx({ bg: "#FBD9DE !important", text: "#B93544" }, { bg: "#FBD9DE !important", text: "#B93544" }),
};

const primaryBlue = "#1D54A2";
const readOnlyRadioSx = {
	color: "#B9C7DC",
	"&.Mui-checked": { color: primaryBlue },
	"&.Mui-disabled.Mui-checked": { color: primaryBlue },
	"&.Mui-disabled": { color: "#B9C7DC" },
};

const AgentPreference = ({ currentAgent }) => {
	const { submission, loading, review } = useAgentPreferenceForAgent(currentAgent?.user_id);
	const [pendingAction, setPendingAction] = useState(null); // "approve" | "reject"

	const statusConfig = submission ? STATUS_CONFIG[submission.status] || STATUS_CONFIG.Pending : null;
	const isPending = submission?.status === "Pending";

	const confirmAction = async () => {
		if (!pendingAction || !submission) return;
		await review(submission.id, pendingAction === "approve" ? "Approved" : "Rejected");
		setPendingAction(null);
	};

	return (
		<Paper variant="outlined" sx={{ borderRadius: 2, width: "100% !important" }}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				px={2}
				py={1.5}
				sx={{ backgroundColor: "secondary.lightbgcolor" }}
			>
				<Typography fontWeight={600} color="text.primary">
					Agent Preference
				</Typography>
				{statusConfig && (
					<Chip label={statusConfig.label} size="small" sx={{ bgcolor: statusConfig.bg, color: statusConfig.color, fontWeight: 700 }} />
				)}
			</Stack>
			<Divider />

			<Box p={2}>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress size={24} sx={{ color: primaryBlue }} />
					</Box>
				) : !submission ? (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 3, color: "text.secondary" }}>
						<InfoOutlinedIcon sx={{ fontSize: 32, color: "#B9C7DC" }} />
						<Typography variant="body2" color="text.secondary">
							This agent hasn't submitted any preferences yet.
						</Typography>
					</Box>
				) : (
					<>
						<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
							Submitted {dayjs(submission.submitted_at).format("MMMM D, YYYY [at] h:mm A")}
						</Typography>

						{isPending && (
							<Stack direction="row" spacing={1} mb={2}>
								<Button
									size="small"
									variant="contained"
									startIcon={<CheckCircleOutlineIcon />}
									onClick={() => setPendingAction("approve")}
									sx={approveButtonSx}
								>
									Approve
								</Button>
								<Button
									size="small"
									variant="contained"
									startIcon={<HighlightOffIcon />}
									onClick={() => setPendingAction("reject")}
									sx={rejectButtonSx}
								>
									Reject
								</Button>
							</Stack>
						)}

						<Stack spacing={1} mb={2.5}>
							{(submission.answers || []).map((answer) => (
								<Box
									key={answer.catalog_id ?? answer.label}
									sx={{
										display: "grid",
										gridTemplateColumns: "1fr auto",
										alignItems: "center",
										columnGap: 2,
									}}
								>
									<Typography variant="body2" fontWeight={600}>
										{answer.label}
									</Typography>
									<RadioGroup row value={answer.answer ? "yes" : "no"} sx={{ flexWrap: "nowrap" }}>
										<FormControlLabel
											value="yes"
											disabled
											control={<Radio size="small" sx={readOnlyRadioSx} />}
											label="Yes"
											sx={{ mr: 1, whiteSpace: "nowrap" }}
										/>
										<FormControlLabel
											value="no"
											disabled
											control={<Radio size="small" sx={readOnlyRadioSx} />}
											label="No"
											sx={{ mr: 0, whiteSpace: "nowrap" }}
										/>
									</RadioGroup>
								</Box>
							))}
						</Stack>

						<Typography variant="subtitle2" fontWeight={600} mb={1}>
							Remarks
						</Typography>
						<RichEditor value={submission.remarks || EMPTY_CONTENT} readOnly />
					</>
				)}
			</Box>

			<ConfirmationModal
				open={!!pendingAction}
				onClose={() => setPendingAction(null)}
				onConfirm={confirmAction}
				title={pendingAction === "approve" ? "Approve Preference Change?" : "Reject Preference Change?"}
				description={
					pendingAction === "approve"
						? `Approve ${currentAgent?.display_name || "this agent"}'s submitted preferences?`
						: `Reject ${currentAgent?.display_name || "this agent"}'s submitted preferences?`
				}
				confirmText={pendingAction === "approve" ? "Yes, Approve" : "Yes, Reject"}
				cancelText="Cancel"
				confirmColor={pendingAction === "approve" ? "#1E7E4D" : "#B93544"}
			/>
		</Paper>
	);
};

export default AgentPreference;
